import {matches} from 'lodash';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {isElectron} from '../../../libs/shared/helpers/src/environment/platformHelpers';
import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {makeMapStateToProps} from '../../helpers/reselectHelpers';
import {toggleExpandItemAction} from '../../store/data/localPreferences/localPreferencesActions';
import {getFindIsExpanded} from '../../store/data/localPreferences/localPreferencesSelectors';
import {getIsUserGlobalAdmin} from '../../store/data/teammates/teammatesSelectors';
import {
  AppContentTypesEnum,
  defaultPathsByContentType,
} from '../../store/panels/app/parameters/appParameters';
import {getSettingsMenuBlocks} from '../../store/settings/settingsMenuSelectors';
import {getUserDefaultSettingsPath} from '../settingsContainer';
import {
  buildSettingsSectionExpandedKey,
  isOnPersonalRoute,
  isSettingsPathItemMatchingPathId,
  makeSettingsItemFromLocationPathAndQuery,
  SettingsPathId,
} from './settingsHelpers';
import {SettingsMenu} from './settingsMenu';
import {SettingsMenuItem, SettingsMenuItemsBlock} from './settingsMenuCommonTypes';

/*
 * Constants.
 */

const defaultSettingsPath = defaultPathsByContentType[AppContentTypesEnum.SETTINGS];

/*
 * Props.
 */

interface SettingsMenuContainerProps {
  settingsPath: string;
}

/*
 * Selectors.
 */

function getLocationPathAndQuery(_state: unknown, props: SettingsMenuContainerProps) {
  return props.settingsPath;
}

function makeIsCurrentItemActive() {
  return createSelector(
    getLocationPathAndQuery,
    (locationPathAndQuery) =>
      (pathId: SettingsPathId, parameters: SettingsMenuItem['parameters'] = {}) => {
        const itemFromLocationPathAndQuery = makeSettingsItemFromLocationPathAndQuery(locationPathAndQuery);

        if (!itemFromLocationPathAndQuery) {
          return false;
        }

        const isPathIdMatching = isSettingsPathItemMatchingPathId(itemFromLocationPathAndQuery, pathId);

        const matchesLocationParameters = matches(itemFromLocationPathAndQuery.parameters);
        const matchesItemParameters = matches(parameters);

        return (
          isPathIdMatching &&
          (matchesLocationParameters(parameters) ||
            matchesItemParameters(itemFromLocationPathAndQuery.parameters))
        );
      },
  );
}

function makeGetIsOnPersonalRoute() {
  return createSelector(
    [getLocationPathAndQuery, getUserDefaultSettingsPath],
    (locationPathAndQuery, userDefaultSettingsPath) =>
      isOnPersonalRoute(
        // If at the root level route, instead check the default route that the user will be redirected to.
        locationPathAndQuery === defaultSettingsPath ? userDefaultSettingsPath : locationPathAndQuery,
      ),
  );
}

function makeFindIsExpanded() {
  return createSelector(
    getFindIsExpanded,
    (findIsExpanded) => (block: SettingsMenuItemsBlock) =>
      findIsExpanded(buildSettingsSectionExpandedKey(block.sectionId), true),
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<SettingsMenuContainerProps>()(() => ({
  isGlobalAdmin: getIsUserGlobalAdmin,
  isCurrentItemActive: makeIsCurrentItemActive(),
  settingsMenuBlocks: getSettingsMenuBlocks,
  isOnPersonalRoute: makeGetIsOnPersonalRoute(),
  findIsExpanded: makeFindIsExpanded(),
  locationPathAndQuery: getLocationPathAndQuery,
}));

/*
 * Component.
 */

export const SettingsMenuContainer = connect(
  mapStateToProps,
  {toggleExpandItemAction},
  (stateProps, dispatchProps, ownProps) => {
    const onBlockCollapseClick: HandlerOf<SettingsMenuItemsBlock> = (block) => {
      dispatchProps.toggleExpandItemAction(buildSettingsSectionExpandedKey(block.sectionId), true);
    };

    return {
      ...stateProps,
      ...ownProps,
      onBlockCollapseClick,
      isOnElectron: isElectron(),
    };
  },
)(SettingsMenu);
